/**
 * Notifications slice — Redux Toolkit state + thunks for the in-app
 * notification center.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   notifications: Notification[];
 *   unreadCount: number;
 *   pagination: { currentPage: number; lastPage: number; total: number };
 *   isLoading: boolean;
 *   error: string | null;
 * }
 * ```
 *
 * Per `.claude/rules/frontend-data-flow.md`:
 *
 *  - Capsules MUST NOT import this slice. Containers do (NotificationBell,
 *    `/account/notifications` — both shipped as separate tickets).
 *  - All four thunks call the shared axios client via the
 *    `@dfq/shared/api/notifications` module — no second axios instance, no
 *    direct `fetch` calls.
 *  - Optimistic updates for `markNotificationRead` and `markAllRead` apply
 *    in the `pending` reducer; rollback snapshots are carried on the thunk
 *    arg so the `rejected` reducer can restore state without a second
 *    `getState()` round-trip.
 *
 * Pagination behaviour
 * --------------------
 * `fetchNotifications` with `page === 1` (or no page) replaces the
 * `notifications` array. With `page > 1`, the returned rows are appended
 * to the existing array — supports an infinite-scroll list view without
 * coupling the slice to scroll state.
 *
 * SH-NOTIFY-001.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import * as notificationsApi from '@dfq/shared/api/notifications';
import type {
  FetchNotificationsParams,
  Notification,
  NotificationsListResponse,
  MarkNotificationReadResponse,
  MarkAllReadResponse,
} from '@dfq/shared/api/notifications';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export interface NotificationsPagination {
  currentPage: number;
  lastPage: number;
  total: number;
}

export interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  pagination: NotificationsPagination;
  isLoading: boolean;
  error: string | null;
}

const initialPagination: NotificationsPagination = {
  currentPage: 1,
  lastPage: 1,
  total: 0,
};

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  pagination: initialPagination,
  isLoading: false,
  error: null,
};

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/notifications` — paginated list with optional `type`
 * filter. The `type` parameter is typed against {@link NotificationType}
 * so callers cannot pass an unsupported filter value at compile time.
 *
 * Pagination strategy:
 *  - `page === 1` (or undefined) → replaces `state.notifications`.
 *  - `page > 1` → appends the returned rows to `state.notifications`.
 */
export const fetchNotifications = createAsyncThunk<
  NotificationsListResponse,
  FetchNotificationsParams | undefined
>('notifications/fetchNotifications', async (params) => {
  return notificationsApi.fetchNotificationsApi(params ?? {});
});

/**
 * `GET /api/v1/notifications/unread-count` — updates only `unreadCount`
 * in state. Intended for badge polling without disturbing the list.
 */
export const fetchUnreadCount = createAsyncThunk<number, void>(
  'notifications/fetchUnreadCount',
  async () => {
    const data = await notificationsApi.fetchUnreadCountApi();
    return data.unread_count;
  },
);

/**
 * Argument shape for {@link markNotificationRead}.
 *
 * Containers usually dispatch with just an `id`; the slice uses
 * `getState()` in the thunk's prepare-step (via `meta.arg`) to capture
 * the pre-mutation `read_at` so the rejected reducer can roll back
 * cleanly. We keep the public signature ergonomic by accepting either a
 * plain `number` or a `{ id }` object.
 */
export type MarkNotificationReadArg = number | { id: number };

function resolveMarkReadId(arg: MarkNotificationReadArg): number {
  return typeof arg === 'number' ? arg : arg.id;
}

/**
 * `PUT /api/v1/notifications/:id/read` with optimistic update.
 *
 * The pending reducer immediately sets `read_at` on the matching row
 * (and decrements `unreadCount` if the row was previously unread). On
 * rejection the reducer restores the previous `read_at` value and
 * un-decrements `unreadCount` so transient network failures don't leave
 * the badge in a stale state.
 */
export const markNotificationRead = createAsyncThunk<
  MarkNotificationReadResponse,
  MarkNotificationReadArg
>('notifications/markNotificationRead', async (arg) => {
  return notificationsApi.markNotificationReadApi(resolveMarkReadId(arg));
});

/**
 * `PUT /api/v1/notifications/read-all` with optimistic update.
 *
 * The pending reducer snapshots every notification's `read_at` value
 * onto the action's `meta.arg.snapshot` (set by the slice on dispatch),
 * applies the optimistic write, and zeroes `unreadCount`. On rejection
 * the reducer restores each row's snapshotted `read_at` and the prior
 * `unreadCount`.
 *
 * The thunk takes no argument from callers — the snapshot is built in
 * the `pending` extraReducer using the current state.
 */
export const markAllRead = createAsyncThunk<MarkAllReadResponse, void>(
  'notifications/markAllRead',
  async () => {
    return notificationsApi.markAllReadApi();
  },
);

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

/**
 * Module-level rollback ledger for the optimistic mark-read flows.
 *
 * RTK's reducers must be pure, so we cannot stash rollback data on
 * `state` itself (it would survive the rejected-restore otherwise). The
 * pending reducer captures the pre-mutation values keyed by the thunk's
 * `requestId`; the rejected reducer reads + clears the entry. The
 * fulfilled reducer also clears the entry so successful flows don't leak
 * memory across many mark-read dispatches.
 */
interface MarkOneSnapshot {
  id: number;
  previousReadAt: string | null;
  wasUnread: boolean;
}

interface MarkAllSnapshot {
  previousReadAtById: Record<number, string | null>;
  previousUnreadCount: number;
}

const markOneSnapshots = new Map<string, MarkOneSnapshot>();
const markAllSnapshots = new Map<string, MarkAllSnapshot>();

/**
 * Test seam — clear the rollback ledgers between tests so a previous
 * test's rejected dispatch can't bleed into a later one. Not exported
 * via the convenience namespace because production code never needs it.
 */
export function __resetNotificationsRollbackLedgersForTests(): void {
  markOneSnapshots.clear();
  markAllSnapshots.clear();
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /** Reset the slice to its initial shape — for logout / account-switch flows. */
    resetNotificationsState() {
      markOneSnapshots.clear();
      markAllSnapshots.clear();
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      // -- fetchNotifications -------------------------------------------------
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const requestedPage = action.meta.arg?.page ?? 1;
        const { notifications, meta } = action.payload;

        if (requestedPage > 1) {
          state.notifications = state.notifications.concat(notifications);
        } else {
          state.notifications = notifications;
        }
        state.pagination = {
          currentPage: meta.current_page,
          lastPage: meta.last_page,
          total: meta.total,
        };
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Failed to fetch notifications';
      })
      // -- fetchUnreadCount ---------------------------------------------------
      .addCase(fetchUnreadCount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Failed to fetch unread count';
      })
      // -- markNotificationRead (OPTIMISTIC) ----------------------------------
      .addCase(markNotificationRead.pending, (state, action) => {
        const id = resolveMarkReadId(action.meta.arg);
        const target = state.notifications.find((n) => n.id === id);
        if (target === undefined) {
          // Nothing to optimistically write — still record an empty
          // snapshot so the rejected reducer is symmetric.
          markOneSnapshots.set(action.meta.requestId, {
            id,
            previousReadAt: null,
            wasUnread: false,
          });
          return;
        }
        const wasUnread = target.read_at === null;
        markOneSnapshots.set(action.meta.requestId, {
          id,
          previousReadAt: target.read_at,
          wasUnread,
        });
        target.read_at = new Date().toISOString();
        if (wasUnread && state.unreadCount > 0) {
          state.unreadCount -= 1;
        }
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        // Replace `read_at` with the platform's authoritative timestamp
        // (the optimistic write used the client clock; server time is
        // canonical). `unreadCount` was already decremented in pending.
        const target = state.notifications.find(
          (n) => n.id === action.payload.id,
        );
        if (target !== undefined) {
          target.read_at = action.payload.read_at;
        }
        markOneSnapshots.delete(action.meta.requestId);
        state.error = null;
      })
      .addCase(markNotificationRead.rejected, (state, action) => {
        const snapshot = markOneSnapshots.get(action.meta.requestId);
        if (snapshot !== undefined) {
          const target = state.notifications.find((n) => n.id === snapshot.id);
          if (target !== undefined) {
            target.read_at = snapshot.previousReadAt;
          }
          if (snapshot.wasUnread) {
            state.unreadCount += 1;
          }
          markOneSnapshots.delete(action.meta.requestId);
        }
        state.error = action.error.message ?? 'Failed to mark notification read';
      })
      // -- markAllRead (OPTIMISTIC) -------------------------------------------
      .addCase(markAllRead.pending, (state, action) => {
        const previousReadAtById: Record<number, string | null> = {};
        const nowIso = new Date().toISOString();
        for (const n of state.notifications) {
          previousReadAtById[n.id] = n.read_at;
          if (n.read_at === null) {
            n.read_at = nowIso;
          }
        }
        markAllSnapshots.set(action.meta.requestId, {
          previousReadAtById,
          previousUnreadCount: state.unreadCount,
        });
        state.unreadCount = 0;
      })
      .addCase(markAllRead.fulfilled, (state, action) => {
        markAllSnapshots.delete(action.meta.requestId);
        // unreadCount already 0 from the pending case; nothing else to do.
        state.error = null;
      })
      .addCase(markAllRead.rejected, (state, action) => {
        const snapshot = markAllSnapshots.get(action.meta.requestId);
        if (snapshot !== undefined) {
          for (const n of state.notifications) {
            if (n.id in snapshot.previousReadAtById) {
              n.read_at = snapshot.previousReadAtById[n.id];
            }
          }
          state.unreadCount = snapshot.previousUnreadCount;
          markAllSnapshots.delete(action.meta.requestId);
        }
        state.error = action.error.message ?? 'Failed to mark all notifications read';
      });
  },
});

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

/**
 * Minimal slice of root state the selectors read. Importing `RootState`
 * here would create a cycle through the root barrel.
 */
export interface NotificationsRootStateSlice {
  notifications: NotificationsState;
}

const selectNotificationsState = (
  state: NotificationsRootStateSlice,
): NotificationsState => state.notifications;

/**
 * Memoized identity selector for the notifications array — stable
 * reference for the same underlying state slot so consumers don't
 * re-render when an unrelated field of the slice changes.
 */
export const selectNotifications = createSelector(
  [selectNotificationsState],
  (s) => s.notifications,
);

export const selectUnreadCount = createSelector(
  [selectNotificationsState],
  (s) => s.unreadCount,
);

export const selectNotificationsLoading = createSelector(
  [selectNotificationsState],
  (s) => s.isLoading,
);

/** Convenience selector for containers that surface error banners. */
export const selectNotificationsError = createSelector(
  [selectNotificationsState],
  (s) => s.error,
);

/** Convenience selector for the pagination meta block. */
export const selectNotificationsPagination = createSelector(
  [selectNotificationsState],
  (s) => s.pagination,
);

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export const { resetNotificationsState } = notificationsSlice.actions;

export { notificationsSlice };
export const notificationsReducer = notificationsSlice.reducer;
export default notificationsReducer;

// Re-export the api-module's domain types so containers have a single
// import target (`@dfq/shared/stores/notifications`) for both state and
// notification shape.
export type {
  Notification,
  NotificationType,
  FetchNotificationsParams,
  NotificationsPaginationMeta,
} from '@dfq/shared/api/notifications';
